import React, { useCallback, useMemo, useState } from "react";
import { Task, TaskStatus } from "@common/types/task";
import { pluralize } from "@common/helpers/string";
import {
    FinancialDocument,
    FinancialDocumentListItem,
} from "@common/types/financialDocument";
import { Button } from "@design-system/Button/Button";
import { useFinancialDocumentUpload } from "@components/documents/FinancialDocumentUpload/useFinancialDocumentUpload";
import { StandardModal } from "@design-system/Modal/Modal";
import { FinancialDocumentSelect } from "@components/documents/FinancialDocumentSelect/FinancialDocumentSelect";
import { DocumentSelectVariant } from "@components/documents/FinancialDocumentSelect/financialDocumentSelectViewConfig";
import {
    useAccountingMigration,
    useUpdateMigrationStartDateMutation,
} from "@api/accountingIntegration.api";
import { format, parseISO } from "date-fns";
import { useAccrualLedgerStatus } from "@api/ledger.api";
import { useEntitiesById } from "@hooks/useEntities";
import { useScope } from "state/scopeContext.lib";
import {
    migrationRequiredPeriod,
    type MigrationRequiredPeriod,
} from "@common/helpers/migrationRequiredPeriod";
import { MigrationFinalizeWarningModal } from "@components/accounting/MigrationFinalizeWarningModal";
import { useMigrationFileRows } from "@components/accounting/migrationFileUploadShared";
import { CoADocumentMigrationSource } from "@common/schemas/accounting-integration.schema";
import { useTaskDocumentLinkHandlers } from "@components/tasks/useTaskDocumentLinkHandlers";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import styles from "./MigrationFileUploadTaskBody.module.scss";
import { useMigrationFinalizeGating } from "./useMigrationFinalizeGating";
import { MigrationGrids } from "./MigrationGrids";
import { MigrationCommandBar } from "./MigrationCommandBar";
import { useMigrationUploadGrids } from "./useMigrationUploadGrids";
import { useMigrationUploadMigrate } from "./useMigrationUploadMigrate";
import { QbdUploadPathFallbackButton } from "./QbdUploadPathFallbackButton";

interface MigrationFileUploadTaskBodyProps {
    selectedTask: Task;
    source: CoADocumentMigrationSource;
    onUploadRef: React.MutableRefObject<
        ((docs: FinancialDocument[]) => void) | undefined
    >;
}

export const MigrationFileUploadTaskBody: React.FC<
    MigrationFileUploadTaskBodyProps
> = ({ selectedTask, source, onUploadRef }) => {
    const { workspaceId } = useScope();
    const entityId = selectedTask.entityIds?.[0];
    const { handleLinkDocument, handleUnlinkDocuments } =
        useTaskDocumentLinkHandlers(selectedTask);
    const { hasAccrualLedger, isLoading: isLedgersLoading } =
        useAccrualLedgerStatus(entityId);
    const isAccrualLedgerResolved = !isLedgersLoading;

    const migrationQuery = useAccountingMigration(
        workspaceId ?? null,
        entityId,
    );
    const entitiesById = useEntitiesById();
    const entity = entityId ? entitiesById.get(entityId) : undefined;

    const requiredPeriod = useMemo<MigrationRequiredPeriod | null>(
        () =>
            migrationRequiredPeriod({
                migrationStartDate: migrationQuery.data?.startDate,
                bookkeepingStartDate: entity?.bookkeepingStartDate,
            }),
        [migrationQuery.data?.startDate, entity?.bookkeepingStartDate],
    );

    const {
        docs,
        addDocument,
        removeDoc,
        setDocBasis,
        dismissParserWarning,
        isAnyExtracting,
    } = useMigrationFileRows({
        workspaceId: workspaceId ?? "",
        entityId: entityId ?? 0,
        source,
        restoreDocuments: selectedTask.financialDocuments,
        onLinkDocument: handleLinkDocument,
        onUnlinkDocuments: handleUnlinkDocuments,
    });

    const isCompleted = selectedTask.status === TaskStatus.DONE;

    const {
        displayRows,
        unverifiedDocs,
        matchedCount,
        migrationDocumentIds,
        finalizeWarningMessages,
        finalizeWarningDescription,
        finalizeDisabledReason,
        canFinalize,
        hasFinalizeWarnings,
        hasStartDateWarning,
        earliestGlStart,
        refetchValidation,
    } = useMigrationFinalizeGating({
        workspaceId: workspaceId ?? "",
        entityId: entityId ?? 0,
        source,
        docs,
        requiredPeriod,
        hasAccrualLedger,
        isAccrualLedgerResolved,
        isAnyExtracting,
        isCompleted,
    });

    const [showFinalizeWarning, setShowFinalizeWarning] = useState(false);

    const { migrateMutation, handleMigrate, handleFinalizeClick } =
        useMigrationUploadMigrate({
            workspaceId: workspaceId ?? "",
            entityId: entityId ?? 0,
            source,
            selectedTask,
            migrationDocumentIds,
            refetchValidation,
            onFinalizeWarningNeeded: () => setShowFinalizeWarning(true),
            hasFinalizeWarnings,
        });

    const { mainData, unverifiedData } = useMigrationUploadGrids({
        source,
        displayRows,
        unverifiedDocs,
        onRemoveDoc: removeDoc,
        onSelectBasis: setDocBasis,
        onDismissParserWarning: dismissParserWarning,
        isMigrating: migrateMutation.isPending,
        isCompleted,
    });

    onUploadRef.current = (uploadedDocs: FinancialDocument[]) => {
        for (const doc of uploadedDocs) {
            addDocument(doc.id, doc.fileName);
        }
    };

    const updateStartDateMutation = useUpdateMigrationStartDateMutation({
        workspaceId: workspaceId ?? "",
        entityId: entityId ?? 0,
    });
    const migrationStartDate = migrationQuery.data?.startDate ?? null;
    const isMigrationLocked =
        migrationQuery.data?.accountsMappingFinalizedAt != null;
    const adoptFileStartDate =
        hasStartDateWarning &&
        earliestGlStart !== null &&
        earliestGlStart !== migrationStartDate &&
        !isMigrationLocked
            ? {
                  label: `Set start date to ${format(
                      parseISO(earliestGlStart),
                      "MMM d, yyyy",
                  )}`,
                  onAdopt: () =>
                      updateStartDateMutation.mutate(earliestGlStart),
                  isAdopting: updateStartDateMutation.isPending,
              }
            : undefined;

    const { inputProps, open: openFilePicker } = useFinancialDocumentUpload();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const [showDocumentModal, setShowDocumentModal] = useState(false);

    const handleBrowseDocuments = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        setShowDocumentModal(true);
    }, [refuseIfViewOnly]);

    const attachedDocumentIds = useMemo(
        () => docs.map((doc) => doc.documentId),
        [docs],
    );
    const attachedDocumentIdSet = useMemo(
        () => new Set(attachedDocumentIds),
        [attachedDocumentIds],
    );

    const renderAttachCta = useCallback(
        (financialDocument: FinancialDocumentListItem) => {
            const isAttached = attachedDocumentIdSet.has(financialDocument.id);
            return (
                <Button
                    variant="primary"
                    size="sm"
                    disabled={isAttached}
                    onClick={() =>
                        addDocument(
                            financialDocument.id,
                            financialDocument.fileName,
                        )
                    }
                >
                    {isAttached ? "Attached" : "Attach"}
                </Button>
            );
        },
        [attachedDocumentIdSet, addDocument],
    );

    const subtitle = `Matched ${matchedCount} ${pluralize(
        "document",
        matchedCount,
    )}`;

    if (!workspaceId || !entityId) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={styles.subtitle}>
                <span>{subtitle}</span>
            </div>

            <MigrationGrids
                mainData={mainData}
                unverifiedData={unverifiedData}
            />

            <input {...inputProps} />

            <StandardModal
                className="fullscreen-modal"
                show={showDocumentModal}
                onHide={() => setShowDocumentModal(false)}
            >
                <FinancialDocumentSelect
                    variant={DocumentSelectVariant.DOCUMENT_ATTACHMENT}
                    attachedDocumentIds={attachedDocumentIds}
                    initialFilters={{ fileExtensions: ["csv", "xlsx"] }}
                    cta={renderAttachCta}
                />
            </StandardModal>

            <MigrationFinalizeWarningModal
                show={showFinalizeWarning}
                warnings={finalizeWarningMessages}
                description={finalizeWarningDescription}
                onCancel={() => setShowFinalizeWarning(false)}
                onConfirm={handleMigrate}
                isConfirming={migrateMutation.isPending}
            />

            {!isCompleted && (
                <MigrationCommandBar
                    finalizeWarningMessages={finalizeWarningMessages}
                    finalizeDisabledReason={finalizeDisabledReason}
                    canFinalize={canFinalize}
                    isMigrating={migrateMutation.isPending}
                    adoptFileStartDate={adoptFileStartDate}
                    extraActions={
                        source === "qbdt" ? (
                            <QbdUploadPathFallbackButton
                                selectedTask={selectedTask}
                                source="qbdt"
                                disabled={migrateMutation.isPending}
                            />
                        ) : undefined
                    }
                    onUploadDocument={openFilePicker}
                    onBrowseDocuments={handleBrowseDocuments}
                    onFinalize={handleFinalizeClick}
                />
            )}
        </div>
    );
};
